import { ref, computed } from 'vue'
import apiService from '@/services/api'
import type { SupersearchResponse, SupersearchDomain } from '@/types/supersearch'

// Debounce timer
let debounceTimer: ReturnType<typeof setTimeout> | null = null

export function useSupersearch() {
  const query = ref('')
  const results = ref<SupersearchDomain[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const loadingSlowDomains = ref(false)

  /**
   * Perform search with debouncing
   * Fast models are searched first, then slow models (operations-jobs, sales-history) are searched separately
   */
  const search = async (limit: number = 5) => {
    const searchQuery = query.value.trim()
    
    // Only search if query has at least 2 non-space characters
    if (searchQuery.length < 2) {
      results.value = []
      error.value = null
      return
    }

    // Prevent duplicate requests
    if (loading.value) {
      return
    }

    loading.value = true
    loadingSlowDomains.value = false
    error.value = null

    try {
      // First, search fast models (exclude slow models)
      const fastResponse = await apiService.get<SupersearchResponse>('/supersearch', {
        params: {
          q: searchQuery,
          limit,
          exclude: 'operations-jobs,sales-history'
        },
        timeout: 10000 // 10 second timeout
      })
      
      // Set fast results immediately
      results.value = fastResponse.data || []
      error.value = null

      // Then search slow models asynchronously (don't block UI)
      // Keep loading.value = true until ALL requests complete
      loadingSlowDomains.value = true
      try {
        const slowResponse = await apiService.get<SupersearchResponse>('/supersearch', {
          params: {
            q: searchQuery,
            limit,
            exclude: 'products,accounts,sub-accounts,tasks,projects,addresses,notes,users,bug-reports'
          },
          timeout: 10000 // 10 second timeout
        })

        // Merge slow domain results with fast results
        if (slowResponse.data && slowResponse.data.length > 0) {
          // Find existing domains and merge, or add new domains
          slowResponse.data.forEach((slowDomain) => {
            const existingDomainIndex = results.value.findIndex(d => d.domain === slowDomain.domain)
            if (existingDomainIndex >= 0) {
              // Merge results if domain already exists
              results.value[existingDomainIndex].results = [
                ...results.value[existingDomainIndex].results,
                ...slowDomain.results
              ]
            } else {
              // Add new domain
              results.value.push(slowDomain)
            }
          })
        }
      } catch (slowErr: any) {
        // Log error but don't show it to user - slow domains are optional
        console.error('Slow domain search failed:', slowErr)
        console.error('Slow domain search error details:', {
          message: slowErr.message,
          response: slowErr.response?.data,
          status: slowErr.response?.status
        })
      } finally {
        loadingSlowDomains.value = false
        // Only set loading to false after ALL requests complete
        loading.value = false
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to search'
      results.value = []
      loading.value = false
      loadingSlowDomains.value = false
    }
  }

  /**
   * Debounced search (300ms delay)
   */
  const debouncedSearch = (limit: number = 5) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    
    debounceTimer = setTimeout(() => {
      search(limit)
    }, 300)
  }

  /**
   * Set query and trigger search
   */
  const setQuery = (value: string) => {
    query.value = value
    
    if (String(value).trim().length >= 2) {
      debouncedSearch()
    } else {
      // Clear results if query is too short
      results.value = []
      error.value = null
      loading.value = false
      
      // Clear any pending debounce
      if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
      }
    }
  }

  /**
   * Clear search state
   */
  const clear = () => {
    query.value = ''
    results.value = []
    error.value = null
    loading.value = false
    
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  /**
   * Check if there are any results
   */
  const hasResults = computed(() => {
    return results.value.length > 0 && results.value.some(domain => domain.results.length > 0)
  })

  /**
   * Get total result count
   */
  const totalResults = computed(() => {
    return results.value.reduce((total, domain) => total + domain.results.length, 0)
  })

  return {
    query,
    setQuery,
    results,
    loading,
    loadingSlowDomains,
    error,
    search,
    debouncedSearch,
    clear,
    hasResults,
    totalResults
  }
}


